/* Wafel-Bitte theme — interaction layer (vanilla JS, no framework).
   Mirrors the Next.js build: scroll reveals, split-text, header pill, overlays,
   AJAX cart with Section Rendering, predictive search, mix switcher, pinned
   story, scroll-driven "Dress up" strip, parallax, gallery, variant picker.
   Everything respects prefers-reduced-motion and works without JS (content is
   visible, forms post natively). */
(function () {
  "use strict";
  const doc = document;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const finePointer = window.matchMedia("(pointer: fine)").matches;
  const $ = (s, r = doc) => r.querySelector(s);
  const $$ = (s, r = doc) => Array.from(r.querySelectorAll(s));

  /* ---------- Smooth scroll (desktop, fine pointer only) ---------- */
  if (window.Lenis && !reduce && finePointer && doc.body.dataset.smoothScroll !== "false") {
    try { new window.Lenis({ lerp: 0.11, duration: 1.1, smoothWheel: true, anchors: true, autoRaf: true }); } catch (e) { /* no-op */ }
  }

  /* ---------- Reveals ---------- */
  function initReveals(root = doc) {
    const els = $$(".reveal, .reveal-mask, [data-split]:not(.split-immediate)", root);
    if (reduce || !("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("is-visible")); return; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  }
  initReveals();

  /* ---------- Pause looping animations while off-screen ---------- */
  (function () {
    const els = $$("[data-visibility], .marquee, .rotating-badge");
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((entries) => { for (const e of entries) e.target.dataset.offscreen = e.isIntersecting ? "false" : "true"; }, { rootMargin: "120px 0px" });
    els.forEach((el) => { el.dataset.offscreen = "false"; io.observe(el); });
  })();

  /* ---------- Header: floating pill + tone ---------- */
  (function () {
    const header = $("[data-header]");
    if (!header) return;
    const homeTone = header.dataset.homeTone || "light";
    let lastY = window.scrollY, hidden = false;
    const update = () => {
      const y = window.scrollY;
      const scrolled = y > 24;
      header.dataset.scrolled = String(scrolled);
      header.dataset.tone = scrolled ? "light" : homeTone;
      // Hide while scrolling down past the header, reveal on any upward scroll.
      const delta = y - lastY;
      if (y < 80 || delta < -4) hidden = false;
      else if (delta > 6 && y > 160 && !doc.body.style.overflow) hidden = true;
      header.dataset.hidden = String(hidden);
      lastY = y;
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
  })();

  /* ---------- Overlays (menu, search, cart) ---------- */
  const overlays = {};
  let lastFocus = null;
  function openOverlay(name) {
    const ov = $(`[data-overlay="${name}"]`);
    if (!ov) return;
    lastFocus = doc.activeElement;
    Object.keys(overlays).forEach((k) => k !== name && closeOverlay(k));
    ov.classList.add("is-open");
    ov.setAttribute("aria-hidden", "false");
    overlays[name] = ov;
    doc.body.style.overflow = "hidden";
    const panel = $("[role=dialog]", ov);
    const focusTarget = name === "search" ? $("[data-search-input]", ov) : panel;
    setTimeout(() => focusTarget && focusTarget.focus(), 60);
    $$(`[data-open="${name}"]`).forEach((b) => b.setAttribute("aria-expanded", "true"));
  }
  function closeOverlay(name) {
    const ov = overlays[name] || $(`[data-overlay="${name}"]`);
    if (!ov || !ov.classList.contains("is-open")) return;
    ov.classList.remove("is-open");
    ov.setAttribute("aria-hidden", "true");
    delete overlays[name];
    if (!Object.keys(overlays).length) doc.body.style.overflow = "";
    $$(`[data-open="${name}"]`).forEach((b) => b.setAttribute("aria-expanded", "false"));
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  doc.addEventListener("click", (e) => {
    const opener = e.target.closest("[data-open]");
    if (opener) { e.preventDefault(); openOverlay(opener.dataset.open); return; }
    const closer = e.target.closest("[data-close]");
    if (closer) { const ov = closer.closest("[data-overlay]"); if (ov) { if (closer.tagName === "A") { closeOverlay(ov.dataset.overlay); return; } e.preventDefault(); closeOverlay(ov.dataset.overlay); } }
  });
  doc.addEventListener("keydown", (e) => { if (e.key === "Escape") Object.keys(overlays).forEach(closeOverlay); });

  /* ---------- Predictive search ---------- */
  (function () {
    const input = $("[data-search-input]");
    const results = $("[data-search-results]");
    if (!input || !results) return;
    const initial = results.innerHTML;
    let timer = 0, ctrl = null;
    const money = (cents) => (window.Shopify && window.Shopify.formatMoney ? window.Shopify.formatMoney(cents) : "$" + (cents / 100).toFixed(2));
    input.addEventListener("input", () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) { results.innerHTML = initial; return; }
      timer = setTimeout(async () => {
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        try {
          const url = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/"}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article&resources[limit]=6&resources[options][unavailable_products]=last`;
          const res = await fetch(url, { signal: ctrl.signal });
          if (!res.ok) return;
          const data = (await res.json()).resources.results;
          const products = data.products || [], articles = data.articles || [];
          const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
          results.innerHTML = `<div class="search-results">
            <div><p class="t-eyebrow mb-3">Products</p>${products.length ? `<ul class="search-results__products">${products.map((p) => `<li><a href="${p.url}" class="search-hit"><span class="search-hit__img">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : ""}</span><span style="min-width:0"><span class="search-hit__title">${esc(p.title)}</span><span class="search-hit__price">${money(p.price_min ? Math.round(parseFloat(p.price_min) * 100) : 0)}</span></span></a></li>`).join("")}</ul>` : `<p class="text-muted" style="font-size:.875rem">No products match “${esc(q)}”.</p>`}</div>
            <div><p class="t-eyebrow mb-3">Articles</p>${articles.length ? `<ul>${articles.map((a) => `<li><a href="${a.url}" class="search-article">${esc(a.title)}</a></li>`).join("")}</ul>` : `<p class="text-muted" style="font-size:.875rem">No articles found.</p>`}<a href="/search?q=${encodeURIComponent(q)}" class="link-underline mt-4" style="display:inline-block;font-size:.875rem">See all results</a></div>
          </div>`;
        } catch (err) { /* aborted */ }
      }, 180);
    });
  })();

  /* ---------- Quantity steppers ---------- */
  doc.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-qty-dec], [data-qty-inc]");
    if (!btn) return;
    const box = btn.closest("[data-qty]");
    const input = $("[data-qty-input]", box);
    const min = parseInt(input.min || "1", 10), max = parseInt(input.max || "50", 10);
    let v = parseInt(input.value || "1", 10);
    v = btn.hasAttribute("data-qty-dec") ? Math.max(min, v - 1) : Math.min(max, v + 1);
    input.value = v;
    $("[data-qty-dec]", box).disabled = v <= min;
    $("[data-qty-inc]", box).disabled = v >= max;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------- Cart (AJAX + Section Rendering) ---------- */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  async function cartRequest(path, body) {
    const res = await fetch(root + path, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(Object.assign({ sections: "cart-drawer" + ($("#shopify-section-main-cart") ? ",main-cart" : "") }, body)) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || "Something went wrong.");
    return data;
  }
  function swapSection(id, html) {
    const target = $(`#shopify-section-${id}`);
    if (!target || !html) return;
    const tmp = doc.createElement("div");
    tmp.innerHTML = html;
    const fresh = tmp.querySelector(`#shopify-section-${id}`) || tmp.firstElementChild;
    const wasOpen = id === "cart-drawer" && $("[data-overlay=cart]", target)?.classList.contains("is-open");
    target.innerHTML = fresh.innerHTML;
    if (wasOpen) { const ov = $("[data-overlay=cart]", target); ov.classList.add("is-open"); ov.setAttribute("aria-hidden", "false"); overlays.cart = ov; }
    initReveals(target);
  }
  function applyCart(data) {
    const sections = data.sections || {};
    swapSection("cart-drawer", sections["cart-drawer"]);
    swapSection("main-cart", sections["main-cart"]);
    const count = typeof data.item_count === "number" ? data.item_count : null;
    if (count !== null) $$("[data-cart-count]").forEach((el) => { el.textContent = count; el.dataset.count = count; });
    $$("[data-cart-button]").forEach((b) => b.setAttribute("aria-label", `Open cart, ${count ?? ""} items`));
  }
  function showCartError(msg) {
    const box = $("[data-cart-error]");
    if (box) { box.innerHTML = `<span>${msg}</span><button type="button" class="link-underline" data-dismiss>Dismiss</button>`; }
  }
  doc.addEventListener("click", (e) => { if (e.target.closest("[data-dismiss]")) { const b = e.target.closest("[data-cart-error]"); if (b) b.innerHTML = ""; } });

  doc.addEventListener("submit", async (e) => {
    const form = e.target.closest("[data-atc-form]");
    if (!form) return;
    e.preventDefault();
    const btn = $("[data-atc]", form), label = $("[data-atc-label]", form), dot = $("[data-atc-dot]", form);
    const original = label ? label.textContent : "";
    const fd = new FormData(form);
    const id = fd.get("id"), quantity = parseInt(fd.get("quantity") || "1", 10);
    if (!id) return;
    btn.disabled = true; if (label) label.textContent = "Adding…"; if (dot) dot.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" class="spinner"><path d="M12 3a9 9 0 1 0 9 9"/></svg>';
    try {
      const data = await cartRequest("cart/add.js", { items: [{ id: Number(id), quantity }] });
      const cart = await fetch(root + "cart.js").then((r) => r.json());
      applyCart(Object.assign({}, data, { item_count: cart.item_count }));
      btn.classList.add("is-added"); if (label) label.textContent = "Added to cart"; if (dot) dot.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5L20 7"/></svg>';
      openOverlay("cart");
      setTimeout(() => { btn.classList.remove("is-added"); if (label) label.textContent = original; if (dot) dot.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'; }, 1800);
    } catch (err) {
      if (label) label.textContent = original;
      let msg = form.querySelector("[data-atc-error]");
      if (!msg) { msg = doc.createElement("p"); msg.className = "field-error"; msg.setAttribute("role", "alert"); msg.dataset.atcError = ""; form.appendChild(msg); }
      msg.textContent = err.message;
    } finally { btn.disabled = false; }
  });

  let qtyTimer = 0;
  doc.addEventListener("change", (e) => {
    const input = e.target.closest("[data-qty-input]");
    if (!input) return;
    const box = input.closest("[data-qty]");
    if (!box || !box.dataset.line) return;
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(async () => {
      try { const data = await cartRequest("cart/change.js", { id: box.dataset.line, quantity: parseInt(input.value, 10) }); applyCart(data); }
      catch (err) { showCartError(err.message); }
    }, 250);
  });
  doc.addEventListener("click", async (e) => {
    const rm = e.target.closest("[data-remove]");
    if (!rm) return;
    e.preventDefault();
    try { const data = await cartRequest("cart/change.js", { id: rm.dataset.remove, quantity: 0 }); applyCart(data); }
    catch (err) { showCartError(err.message); }
  });
  doc.addEventListener("submit", async (e) => {
    const noteForm = e.target.closest("[data-cart-note]");
    if (!noteForm) return;
    e.preventDefault();
    const note = noteForm.querySelector("textarea").value;
    try { await fetch(root + "cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note }) }); const s = $("[data-note-saved]", noteForm); if (s) s.hidden = false; }
    catch (err) { showCartError("Could not save the note."); }
  });
  doc.addEventListener("submit", (e) => {
    const dForm = e.target.closest("[data-discount-form]");
    if (!dForm) return;
    e.preventDefault();
    const code = dForm.querySelector("input").value.trim();
    window.location.href = root + "discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(root + "cart");
  });

  /* ---------- Product form: variants, price, availability ---------- */
  $$("[data-product-form]").forEach((form) => {
    const dataEl = $("[data-variants-json]", form);
    if (!dataEl) return;
    const variants = JSON.parse(dataEl.textContent);
    const idInput = $("input[name=id]", form);
    const priceBox = $("[data-price]", form);
    const btn = $("[data-atc]", form), label = $("[data-atc-label]", form), dot = $("[data-atc-dot]", form);
    const soldOut = $("[data-sold-out]", form);
    const money = (c) => (window.Shopify && window.Shopify.formatMoney ? window.Shopify.formatMoney(c) : "$" + (c / 100).toFixed(2));
    function current() {
      const groups = $$("[data-option]", form);
      if (!groups.length) return variants[0] || null;
      const picked = groups.map((g) => { const c = $("input:checked", g); return c ? c.value : null; });
      return variants.find((v) => v.options.every((o, i) => o === picked[i])) || null;
    }
    function render() {
      const v = current();
      if (!v) { btn.disabled = true; if (label) label.textContent = "Unavailable"; return; }
      idInput.value = v.id;
      btn.disabled = !v.available;
      if (label) label.textContent = v.available ? (form.dataset.giftCard === "true" ? "Add gift card" : "Add to cart") : "Sold out";
      if (dot) dot.style.display = v.available ? "" : "none";
      if (soldOut) soldOut.hidden = v.available;
      if (priceBox) {
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        const pct = sale ? Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) : 0;
        priceBox.innerHTML = `<span class="price price--xl"><span class="price__amount"><span class="sr-only">Price </span>${money(v.price)}</span>${sale ? `<s class="price__compare"><span class="sr-only">Regular price </span>${money(v.compare_at_price)}</s><span class="price__save">Save ${pct}%</span>` : ""}</span>`;
      }
      if (variants.length > 1 && window.history.replaceState) {
        const url = new URL(window.location.href); url.searchParams.set("variant", v.id); window.history.replaceState({}, "", url);
      }
      $$("[data-option-value]", form).forEach((el) => { el.textContent = $("input:checked", el.closest("[data-option]"))?.value || ""; });
    }
    form.addEventListener("change", (e) => { if (e.target.matches("[data-option] input")) render(); });
    const qty = $("[data-qty-input]", form);
    if (qty) qty.addEventListener("change", () => { $("input[name=quantity]", form).value = qty.value; });
    render();
  });

  /* ---------- Hero: mark covered once the next sheet slides over ---------- */
  (function () {
    const hero = $("[data-hero]");
    if (!hero) return;
    let covered = false;
    const onScroll = () => { const next = window.scrollY > hero.offsetHeight; if (next !== covered) { covered = next; hero.dataset.offscreen = String(next); } };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  })();

  /* ---------- Ribbon: run SMIL only while visible ---------- */
  $$("[data-ribbon]").forEach((rb) => {
    const path = $("path", rb), tp = $("textPath", rb);
    if (!path || !tp || reduce) return;
    const len = path.getTotalLength();
    const start = () => { if ($("animate", tp)) return; const a = doc.createElementNS("http://www.w3.org/2000/svg", "animate"); a.setAttribute("attributeName", "startOffset"); a.setAttribute("from", "0"); a.setAttribute("to", String(-Math.round(len / 3))); a.setAttribute("dur", (rb.dataset.duration || 34) + "s"); a.setAttribute("repeatCount", "indefinite"); tp.appendChild(a); };
    const stop = () => { const a = $("animate", tp); if (a) a.remove(); };
    if (!("IntersectionObserver" in window)) { start(); return; }
    new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()), { rootMargin: "80px 0px" }).observe(rb);
  });

  /* ---------- Pinned story: active chapter ---------- */
  $$("[data-story]").forEach((rootEl) => {
    const chapters = $$("[data-chapter]", rootEl);
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((entries) => { for (const e of entries) if (e.isIntersecting) rootEl.dataset.active = e.target.dataset.chapter; }, { rootMargin: "-45% 0px -45% 0px", threshold: 0 });
    chapters.forEach((c) => io.observe(c));
  });

  /* ---------- Mix switcher ---------- */
  $$("[data-mix]").forEach((mix) => {
    mix.addEventListener("click", (e) => {
      const tab = e.target.closest("[role=tab]");
      if (!tab) return;
      const i = tab.dataset.index;
      $$("[role=tab]", mix).forEach((t) => t.setAttribute("aria-selected", String(t === tab)));
      $$("[data-mix-img]", mix).forEach((el) => el.classList.toggle("is-active", el.dataset.mixImg === i));
      $$("[data-mix-content]", mix).forEach((el) => el.classList.toggle("is-active", el.dataset.mixContent === i));
      $$("[data-mix-badge]", mix).forEach((el) => { el.hidden = el.dataset.mixBadge !== i; });
    });
  });

  /* ---------- Scroll-driven horizontal strip (Dress up) ---------- */
  (function () {
    const track = $("[data-dressup-track]"), strip = $("[data-dressup-strip]");
    if (!track || !strip || reduce) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    let raf = 0;
    const update = () => {
      raf = 0;
      if (!mq.matches) { strip.style.transform = ""; return; }
      const r = track.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const p = Math.min(1, Math.max(0, -r.top / total));
      strip.style.transform = `translate3d(${(-58 * p).toFixed(3)}%, 0, 0)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
  })();

  /* ---------- Parallax ---------- */
  (function () {
    const els = $$("[data-parallax]");
    if (!els.length || reduce) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) continue;
        const amount = parseFloat(el.dataset.parallax || "60");
        const p = (vh - r.top) / (vh + r.height); // 0 → 1 as it crosses the viewport
        el.style.transform = `translate3d(0, ${(amount - 2 * amount * p).toFixed(2)}px, 0)`;
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
  })();

  /* ---------- Product gallery ---------- */
  $$("[data-gallery]").forEach((g) => {
    const track = $("[data-gallery-track]", g), thumbs = $$("[data-gallery-thumb]", g), counter = $("[data-gallery-counter]", g);
    const slides = $$("[data-gallery-slide]", g);
    let index = 0;
    const set = (i) => {
      index = (i + slides.length) % slides.length;
      thumbs.forEach((t, k) => t.setAttribute("aria-selected", String(k === index)));
      slides.forEach((s, k) => { s.setAttribute("aria-hidden", String(k !== index)); if (k !== index) s.setAttribute("inert", ""); else s.removeAttribute("inert"); });
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      if (thumbs[index]) thumbs[index].scrollIntoView({ block: "nearest", inline: "nearest" });
    };
    const go = (i) => { const next = (i + slides.length) % slides.length; track.scrollTo({ left: track.clientWidth * next, behavior: reduce ? "auto" : "smooth" }); set(next); };
    thumbs.forEach((t, k) => t.addEventListener("click", () => go(k)));
    $("[data-gallery-prev]", g)?.addEventListener("click", () => go(index - 1));
    $("[data-gallery-next]", g)?.addEventListener("click", () => go(index + 1));
    track.addEventListener("keydown", (e) => { if (e.key === "ArrowRight") go(index + 1); if (e.key === "ArrowLeft") go(index - 1); });
    let raf = 0;
    track.addEventListener("scroll", () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => set(Math.round(track.scrollLeft / track.clientWidth))); }, { passive: true });
    set(0);
  });

  /* ---------- YouTube click-to-play ---------- */
  doc.addEventListener("click", (e) => {
    const b = e.target.closest("[data-yt]");
    if (!b) return;
    const id = b.dataset.yt, title = b.getAttribute("aria-label") || "Video";
    const wrap = b.parentElement;
    wrap.innerHTML = `<iframe src="https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1" title="${title}" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowfullscreen referrerpolicy="strict-origin-when-cross-origin"></iframe>`;
  });

  /* ---------- Collection toolbar: auto-submit checkboxes ---------- */
  doc.addEventListener("change", (e) => { const f = e.target.closest("[data-filter-form]"); if (f && e.target.type === "checkbox") f.requestSubmit ? f.requestSubmit() : f.submit(); });
  doc.addEventListener("change", (e) => { const s = e.target.closest("[data-sort-select]"); if (s) { const url = new URL(window.location.href); url.searchParams.set("sort_by", s.value); window.location.href = url.toString(); } });
})();
